import { Text, type TextProps } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

/**
 * Tweens an integer between its previous and current value over
 * `durationMs` so dashboard metrics gracefully count up/down on
 * auto-refresh instead of snapping to the new value. Falls back
 * to a placeholder when the value is null/undefined.
 *
 * The hook lives inside this component (rather than as a free
 * `useCountUp`) so the conditional fallback render can sit outside
 * the hook call, satisfying React's rules-of-hooks.
 */
export function AnimatedNumber({
  value,
  fallback = '—',
  durationMs = 600,
  ...textProps
}: {
  value: number | null | undefined;
  fallback?: string;
  durationMs?: number;
} & TextProps) {
  // useCountUp is always called (hooks must run on every render);
  // a null/undefined input feeds 0 to the tweener so the easing
  // logic doesn't crash, and we override the displayed text with
  // the fallback below.
  const animated = useCountUp(value ?? 0, durationMs);
  const display = value === null || value === undefined ? fallback : animated;
  // Render as an inline span so the caller can drop multiple
  // AnimatedNumbers inside a parent Text (e.g. "X / Y healthy")
  // without producing invalid nested-block markup.
  return (
    <Text component="span" {...textProps}>
      {display}
    </Text>
  );
}

function useCountUp(target: number, durationMs: number): number {
  const [value, setValue] = useState(target);
  // Track the in-flight tween so retargets mid-animation pick up
  // from the current displayed value rather than restarting from
  // the previous target.
  const tween = useRef({ from: target, to: target, startedAt: 0 });

  useEffect(() => {
    if (tween.current.to === target) return;
    tween.current = { from: value, to: target, startedAt: performance.now() };
    let raf = 0;
    function tick(now: number) {
      const t = Math.min(1, (now - tween.current.startedAt) / durationMs);
      // Ease-out quintic — moves quickly off the previous value, then
      // settles softly into the target so the final couple of frames
      // are visibly slower than the start.
      const eased = 1 - Math.pow(1 - t, 5);
      const next = tween.current.from + (target - tween.current.from) * eased;
      setValue(Math.round(next));
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // We intentionally omit `value` from deps — including it would
    // restart the tween on every interpolated step. The repo's ESLint
    // config doesn't load the react-hooks plugin so the usual
    // exhaustive-deps comment isn't recognised; keep the rationale
    // here for the reader.
  }, [target, durationMs]);

  return value;
}
